// storySource — the story board editor's pure engine (#1416).
//
// A story page (`story.html`) is a body fragment of `pt-*` boxes and prose.
// The editor never serialises the live page back to HTML: hydration
// (story.js) rebuilds the hero, prepends section headings and replaces every
// figure with a provenance span, so a DOM round-trip would rewrite the page.
// Instead every edit is a SURGICAL splice of the source text: parse5, with
// source locations, gives each element its byte range, and an edit rewrites
// only the range it names. Every byte outside that range is identical after
// the edit — the property the tests pin for every operation.
//
// Elements are addressed by ORDINAL: their position in a pre-order walk of the
// fragment's elements. The browser's `main.innerHTML = source` builds the same
// tree (both follow the HTML parsing spec, and parse5 is parsed here with the
// same <main> context element), so `main.querySelectorAll('*')[n]` in the
// iframe is `nodes[n]` here. That is how a click maps to a byte range.
//
// Every operation is `(source, ...) => source`; undo is a stack of strings.

import { parseFragment, defaultTreeAdapter } from 'parse5'

const HTML_NS = 'http://www.w3.org/1999/xhtml'

/** Boxes that arrange others or read no data (story.js STRUCTURE_TAGS). */
export const STRUCTURE_TAGS = ['pt-hero', 'pt-section', 'pt-row', 'pt-kpis', 'pt-text', 'pt-callout', 'pt-figure', 'pt-deck', 'pt-slide', 'pt-layer'] as const
/** Boxes that read one dataset and fill themselves (story.js LEAF_TAGS). Locked in the editor. */
export const LEAF_TAGS = ['pt-kpi', 'pt-num', 'pt-chart', 'pt-table'] as const
/** Top-level boxes that stay where they are: the page opens with its hero and closes with its sources, and a presentation's deck holds the whole page. */
export const FIXED_BLOCK_TAGS = ['pt-hero', 'pt-sources', 'pt-deck'] as const

/** Elements a user edits by typing into them, when they hold only inline content. */
export const PROSE_TAGS = new Set([
  'p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'figcaption', 'dt', 'dd', 'td', 'th', 'caption',
  // The one-line label of a folded note (<details class="pt-more">, #1578 item 18).
  'summary',
])
/** Inline markup that survives an edit inside prose. */
export const INLINE_TAGS = new Set([
  'a', 'abbr', 'b', 'br', 'cite', 'code', 'em', 'i', 'mark', 'q', 's', 'small', 'span', 'strong', 'sub', 'sup', 'u',
])

// ---------------------------------------------------------------------------
// The vocabulary: every box and its named inputs. A copy of story.js's
// story-vocabulary.js; storyKit.test.ts holds it to story-vocabulary.json, as
// the runtime, the lint and the Go save check are held. `text` inputs are
// words a reader sees, `image` a picture in the app, `href` a link, `data`
// which cells a box reads, `flag` present or absent, an array the allowed
// values. On a leaf box only the `text` inputs are the editor's.
// ---------------------------------------------------------------------------

export type InputType = 'text' | 'image' | 'href' | 'data' | 'flag' | string[]

export interface BoxSpec {
  kind: 'structure' | 'leaf' | 'sources'
  required: string[]
  inputs: Record<string, InputType>
  pairs?: [string, string][]
}

export const STORY_VOCABULARY: Record<string, BoxSpec> = {
  'pt-hero': {
    kind: 'structure',
    required: ['title'],
    inputs: {
      title: 'text', subtitle: 'text', meta: 'text', verdict: 'text', bar: 'text',
      button: 'text', 'button-href': 'href', image: 'image',
    },
    pairs: [['button', 'button-href']],
  },
  'pt-section': {
    kind: 'structure',
    required: [],
    inputs: { title: 'text', subtitle: 'text', meta: 'text', image: 'image', tone: ['plain', 'muted', 'band'] },
  },
  'pt-row': { kind: 'structure', required: [], inputs: {} },
  'pt-kpis': { kind: 'structure', required: [], inputs: {} },
  'pt-text': { kind: 'structure', required: [], inputs: { title: 'text', subtitle: 'text', meta: 'text', drill: 'data' } },
  'pt-callout': {
    kind: 'structure',
    required: [],
    inputs: { kind: ['finding', 'caveat', 'quote'], title: 'text', subtitle: 'text', meta: 'text', cite: 'text', drill: 'data' },
  },
  'pt-figure': { kind: 'structure', required: ['src', 'alt'], inputs: { src: 'image', alt: 'text', caption: 'text', drill: 'data' } },
  'pt-kpi': {
    kind: 'leaf',
    required: ['label', 'from', 'row', 'col'],
    inputs: {
      label: 'text', from: 'data', row: 'data', col: 'data', metric: 'data', decimals: 'data', unit: ['percent', 'pp', 'count', 'int', 'p', 'raw'],
      'change-col': 'data', 'change-from': 'data', 'change-row': 'data', 'change-label': 'text',
      'compare-row': 'data', 'compare-col': 'data', 'compare-from': 'data', 'compare-label': 'text',
      drill: 'data',
    },
  },
  'pt-num': { kind: 'leaf', required: ['from', 'row', 'col'], inputs: { from: 'data', row: 'data', col: 'data', metric: 'data', decimals: 'data', unit: ['percent', 'pp', 'count', 'int', 'p', 'raw'] } },
  'pt-chart': {
    kind: 'leaf',
    required: ['type', 'from'],
    inputs: {
      type: ['line', 'bar', 'barh', 'stacked', 'pie', 'heatmap', 'custom'],
      from: 'data', rows: 'data', cols: 'data', x: ['row', 'col'], title: 'text',
      metric: 'data', decimals: 'data', unit: ['percent', 'pp', 'count', 'int', 'p', 'raw'], module: 'data', highlight: 'data', area: 'flag', drill: 'data',
    },
  },
  'pt-table': { kind: 'leaf', required: ['from'], inputs: { from: 'data', title: 'text', drill: 'data', decimals: 'data', columns: 'data' } },
  'pt-sources': { kind: 'sources', required: [], inputs: {} },
  // A presentation: one top-level deck, slides in it, layers in a slide or a layer.
  'pt-deck': { kind: 'structure', required: [], inputs: { title: 'text', subtitle: 'text', meta: 'text', bar: 'text' } },
  'pt-slide': {
    kind: 'structure',
    required: ['title'],
    inputs: { title: 'text', subtitle: 'text', meta: 'text', tone: ['plain', 'muted', 'band'], image: 'image', notes: 'text', question: 'text' },
  },
  'pt-layer': { kind: 'structure', required: ['title'], inputs: { title: 'text', subtitle: 'text', meta: 'text', question: 'text' } },
}

const normaliseUrl = (v: string) => v.replace(/[\t\n\r]/g, '').trim()

/** A link a story page may carry: a #section-id, a page in the app, or an http(s)/mailto link. */
export function hrefOk(value: string): boolean {
  const v = normaliseUrl(value)
  if (!v) return false
  if (v.startsWith('#')) return true
  if (/^(https?:\/\/[^/\s]|mailto:)/i.test(v)) return true
  if (v.startsWith('/')) return false
  return !/^[^/?#]*:/.test(v)
}

/** A picture file inside the app directory (a published page loads nothing from the internet). */
export function imageOk(value: string): boolean {
  const v = normaliseUrl(value)
  if (!v || v.includes(':') || v.includes('\\') || v.startsWith('/')) return false
  if (v.split('/').includes('..')) return false
  return /\.(png|jpe?g|webp|gif|svg|avif)$/i.test(v)
}

const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr'])

export interface AttrLoc {
  start: number
  end: number
}

export interface NodeLoc {
  /** Offset of `<`. */
  start: number
  /** Offset after the element's last byte (after `>` of the end tag, or of the start tag for a void element). */
  end: number
  /** Offset after the start tag's `>`. */
  startTagEnd: number
  /** Offset of the end tag's `<`, or null when the element has no end tag in the source. */
  endTagStart: number | null
  attrs: Record<string, AttrLoc>
}

export interface StoryNode {
  /** Pre-order ordinal among the fragment's elements. */
  idx: number
  /** Lowercased tag name. */
  tag: string
  /** Parent element ordinal, or -1 for a top-level element. */
  parent: number
  children: number[]
  /** Ordinal of the last element in this subtree (== idx for an element with no element children). */
  last: number
  attrs: Record<string, string>
  /** Null for an element the parser created without any source (an implied <tbody>). */
  loc: NodeLoc | null
}

export interface StoryDoc {
  source: string
  nodes: StoryNode[]
  /** Ordinals of the top-level elements, in order. */
  topLevel: number[]
}

// parse5's node shapes, structurally: only what this module reads, so the
// module type-checks against parse5 7 and 8 alike.
interface P5Loc {
  startOffset: number
  endOffset: number
}
interface P5ElementLoc extends P5Loc {
  startTag?: P5Loc & { attrs?: Record<string, P5Loc> }
  endTag?: P5Loc
  attrs?: Record<string, P5Loc>
}
interface P5Node {
  nodeName: string
  tagName?: string
  attrs?: { name: string; value: string }[]
  childNodes?: P5Node[]
  sourceCodeLocation?: P5ElementLoc | null
}

/**
 * Parse a story fragment. Uses a <main> context element, exactly as story.js
 * mounts it (`main.innerHTML = text`), so the element ordinals match the
 * browser's.
 */
export function parseStory(source: string): StoryDoc {
  const context = defaultTreeAdapter.createElement('main', HTML_NS as never, [])
  const frag = parseFragment(context, source, { sourceCodeLocationInfo: true }) as unknown as P5Node
  const nodes: StoryNode[] = []
  const topLevel: number[] = []

  const walk = (children: P5Node[] | undefined, parent: number) => {
    for (const child of children ?? []) {
      if (!child.tagName) continue
      const idx = nodes.length
      const attrs: Record<string, string> = {}
      for (const a of child.attrs ?? []) attrs[a.name] = a.value
      const node: StoryNode = {
        idx,
        tag: child.tagName.toLowerCase(),
        parent,
        children: [],
        last: idx,
        attrs,
        loc: toLoc(child.sourceCodeLocation, child.tagName.toLowerCase()),
      }
      nodes.push(node)
      if (parent === -1) topLevel.push(idx)
      else nodes[parent].children.push(idx)
      walk(child.childNodes, idx)
      node.last = nodes.length - 1
    }
  }
  walk(frag.childNodes, -1)
  return { source, nodes, topLevel }
}

function toLoc(l: P5ElementLoc | null | undefined, tag: string): NodeLoc | null {
  if (!l || !l.startTag) return null
  const attrs: Record<string, AttrLoc> = {}
  const src = l.startTag.attrs ?? l.attrs ?? {}
  for (const [name, a] of Object.entries(src)) attrs[name] = { start: a.startOffset, end: a.endOffset }
  const endTagStart = l.endTag ? l.endTag.startOffset : null
  return {
    start: l.startTag.startOffset,
    end: l.endTag ? l.endTag.endOffset : VOID_TAGS.has(tag) ? l.startTag.endOffset : l.endOffset,
    startTagEnd: l.startTag.endOffset,
    endTagStart,
    attrs,
  }
}

// ---------------------------------------------------------------------------
// Escaping
// ---------------------------------------------------------------------------

/** Escape text for an HTML text node. A no-break space is written as `&nbsp;` so it stays visible in the source. */
export function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/ /g, '&nbsp;')
}

/**
 * Escape an attribute value for the quote character it sits in. A single-quoted
 * value (JSON selectors: `rows='["Sky","EE"]'`) keeps its double quotes
 * literal, so the JSON stays readable to the agent and the lint.
 */
export function escapeAttr(value: string, quote: '"' | "'" = '"'): string {
  const v = value.replace(/&/g, '&amp;')
  return quote === '"' ? v.replace(/"/g, '&quot;') : v.replace(/'/g, '&#39;')
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export class StoryEditError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'StoryEditError'
  }
}

function nodeAt(doc: StoryDoc, idx: number): StoryNode {
  const node = doc.nodes[idx]
  if (!node) throw new StoryEditError(`no element ${idx} in this story`)
  return node
}

function locOf(node: StoryNode): NodeLoc {
  if (!node.loc) throw new StoryEditError(`<${node.tag}> has no source of its own and cannot be edited`)
  return node.loc
}

export function isLeafTag(tag: string): boolean {
  return (LEAF_TAGS as readonly string[]).includes(tag)
}

/** True when `idx` sits inside a leaf box (whose contents hydration replaces). */
export function insideLeaf(doc: StoryDoc, idx: number): boolean {
  for (let p = doc.nodes[idx]?.parent ?? -1; p !== -1; p = doc.nodes[p].parent) {
    if (isLeafTag(doc.nodes[p].tag)) return true
  }
  return false
}

/**
 * An element whose text a user types into: a prose tag with both tags in the
 * source, holding only inline markup and inline figures (`pt-num`), and not
 * inside a leaf box. A list item holding a nested list is not one (its own
 * items are).
 */
export function isEditableProse(doc: StoryDoc, idx: number): boolean {
  const node = doc.nodes[idx]
  if (!node || !PROSE_TAGS.has(node.tag)) return false
  if (!node.loc || node.loc.endTagStart === null) return false
  if (insideLeaf(doc, idx)) return false
  for (let i = idx + 1; i <= node.last; i++) {
    const d = doc.nodes[i]
    if (!d.loc) return false
    if (d.tag === 'pt-num') continue
    if (!INLINE_TAGS.has(d.tag)) return false
    if (!VOID_TAGS.has(d.tag) && d.loc.endTagStart === null) return false
  }
  return true
}

/** The source of one element, start tag to end tag. */
export function outerSource(doc: StoryDoc, idx: number): string {
  const loc = locOf(nodeAt(doc, idx))
  return doc.source.slice(loc.start, loc.end)
}

/** The source between an element's tags. */
export function innerSource(doc: StoryDoc, idx: number): string {
  const loc = locOf(nodeAt(doc, idx))
  if (loc.endTagStart === null) throw new StoryEditError('element has no end tag')
  return doc.source.slice(loc.startTagEnd, loc.endTagStart)
}

/** The start tag's bytes (`<a href="…">`), exactly as written. */
export function startTagSource(doc: StoryDoc, idx: number): string {
  const loc = locOf(nodeAt(doc, idx))
  return doc.source.slice(loc.start, loc.startTagEnd)
}

/** The end tag's bytes (`</a>`), exactly as written, or `</tag>` when the source had none. */
export function endTagSource(doc: StoryDoc, idx: number): string {
  const node = nodeAt(doc, idx)
  const loc = locOf(node)
  return loc.endTagStart === null ? `</${node.tag}>` : doc.source.slice(loc.endTagStart, loc.end)
}

/** Top-level boxes that can be moved, duplicated, hidden or deleted. */
export function isMovableBlock(doc: StoryDoc, idx: number): boolean {
  const node = doc.nodes[idx]
  if (!node || node.parent !== -1) return false
  if (!node.tag.startsWith('pt-')) return false
  if ((FIXED_BLOCK_TAGS as readonly string[]).includes(node.tag)) return false
  return !!node.loc && node.loc.endTagStart !== null
}

/** The top-level block containing `idx` (itself when top-level), or -1. */
export function blockOf(doc: StoryDoc, idx: number): number {
  let i = idx
  while (i !== -1 && doc.nodes[i] && doc.nodes[i].parent !== -1) i = doc.nodes[i].parent
  return doc.nodes[i] ? i : -1
}

export function isHidden(doc: StoryDoc, idx: number): boolean {
  return Object.prototype.hasOwnProperty.call(doc.nodes[idx]?.attrs ?? {}, 'hidden')
}

// ---------------------------------------------------------------------------
// Operations — each returns the new source.
// ---------------------------------------------------------------------------

function splice(source: string, start: number, end: number, replacement: string): string {
  return source.slice(0, start) + replacement + source.slice(end)
}

const isWs = (c: string | undefined) => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f'

function wsBefore(source: string, pos: number): number {
  let p = pos
  while (p > 0 && isWs(source[p - 1])) p--
  return p
}

function wsAfter(source: string, pos: number): number {
  let p = pos
  while (p < source.length && isWs(source[p])) p++
  return p
}

/** Replace the content between an element's tags. */
export function editInner(source: string, idx: number, innerHtml: string, doc = parseStory(source)): string {
  const loc = locOf(nodeAt(doc, idx))
  if (loc.endTagStart === null) throw new StoryEditError('element has no end tag, so its content has no fixed end')
  return splice(source, loc.startTagEnd, loc.endTagStart, innerHtml)
}

interface AttrValueRange {
  start: number
  end: number
  quote: '"' | "'" | null
}

function attrValueRange(source: string, a: AttrLoc): AttrValueRange | null {
  const raw = source.slice(a.start, a.end)
  const eq = raw.indexOf('=')
  if (eq === -1) return null
  let p = a.start + eq + 1
  while (p < a.end && isWs(source[p])) p++
  const q = source[p]
  if (q === '"' || q === "'") return { start: p + 1, end: a.end - 1, quote: q }
  return { start: p, end: a.end, quote: null }
}

/** Set an attribute, keeping its quote character when it already has one. */
export function setAttr(source: string, idx: number, name: string, value: string, doc = parseStory(source)): string {
  const node = nodeAt(doc, idx)
  const loc = locOf(node)
  const key = name.toLowerCase()
  const a = loc.attrs[key]
  if (a) {
    const v = attrValueRange(source, a)
    if (v && v.quote) return splice(source, v.start, v.end, escapeAttr(value, v.quote))
    const written = source.slice(a.start, a.start + key.length)
    return splice(source, a.start, a.end, `${written}="${escapeAttr(value)}"`)
  }
  return splice(source, insertAttrAt(source, node, loc), insertAttrAt(source, node, loc), ` ${key}="${escapeAttr(value)}"`)
}

function insertAttrAt(source: string, node: StoryNode, loc: NodeLoc): number {
  const ends = Object.values(loc.attrs).map((a) => a.end)
  if (ends.length) return Math.max(...ends)
  return loc.start + 1 + node.tag.length
}

/** Remove an attribute and the whitespace before it. */
export function removeAttr(source: string, idx: number, name: string, doc = parseStory(source)): string {
  const loc = locOf(nodeAt(doc, idx))
  const a = loc.attrs[name.toLowerCase()]
  if (!a) return source
  return splice(source, wsBefore(source, a.start), a.end, '')
}

/** Hide or show a block with the `hidden` attribute. */
export function setHidden(source: string, idx: number, hidden: boolean, doc = parseStory(source)): string {
  const node = nodeAt(doc, idx)
  const loc = locOf(node)
  const has = !!loc.attrs.hidden
  if (hidden === has) return source
  if (!hidden) return removeAttr(source, idx, 'hidden', doc)
  const at = insertAttrAt(source, node, loc)
  return splice(source, at, at, ' hidden')
}

function requireBlock(doc: StoryDoc, idx: number): NodeLoc {
  if (!isMovableBlock(doc, idx)) {
    const node = doc.nodes[idx]
    throw new StoryEditError(`<${node?.tag ?? '?'}> is not a section that can be moved, duplicated or deleted`)
  }
  return doc.nodes[idx].loc as NodeLoc
}

/** The movable neighbour of a block, or -1 when it is first/last or the neighbour is fixed. */
export function blockNeighbour(doc: StoryDoc, idx: number, dir: -1 | 1): number {
  const pos = doc.topLevel.indexOf(idx)
  if (pos === -1) return -1
  for (let i = pos + dir; i >= 0 && i < doc.topLevel.length; i += dir) {
    const n = doc.nodes[doc.topLevel[i]]
    if ((FIXED_BLOCK_TAGS as readonly string[]).includes(n.tag)) return -1
    if (isMovableBlock(doc, n.idx)) return n.idx
  }
  return -1
}

/** Swap a block with its movable neighbour; everything between the two stays where it was. */
export function moveBlock(source: string, idx: number, dir: -1 | 1, doc = parseStory(source)): string {
  const a = requireBlock(doc, idx)
  const other = blockNeighbour(doc, idx, dir)
  if (other === -1) return source
  const b = doc.nodes[other].loc as NodeLoc
  const [first, second] = a.start < b.start ? [a, b] : [b, a]
  return (
    source.slice(0, first.start) +
    source.slice(second.start, second.end) +
    source.slice(first.end, second.start) +
    source.slice(first.start, first.end) +
    source.slice(second.end)
  )
}

/** Insert a copy of a block right after it, separated the way it is separated from what precedes it. */
export function duplicateBlock(source: string, idx: number, doc = parseStory(source)): string {
  const loc = requireBlock(doc, idx)
  const lead = source.slice(wsBefore(source, loc.start), loc.start)
  const sep = lead.includes('\n') ? lead : '\n\n'
  return splice(source, loc.end, loc.end, sep + source.slice(loc.start, loc.end))
}

/** Delete a block and the whitespace that separated it from what came before. */
export function deleteBlock(source: string, idx: number, doc = parseStory(source)): string {
  const loc = requireBlock(doc, idx)
  const from = wsBefore(source, loc.start)
  if (from === 0) return splice(source, 0, wsAfter(source, loc.end), '')
  return splice(source, from, loc.end, '')
}

// ---------------------------------------------------------------------------
// Named inputs: what the box settings show and write.
// ---------------------------------------------------------------------------

export interface BlockInput {
  name: string
  /** `choice` is an input with a list of allowed values (`options`). */
  type: 'text' | 'image' | 'href' | 'choice'
  options?: string[]
  /** The value in the source, '' when the input is not written. */
  value: string
  required: boolean
}

/**
 * The inputs of `idx` the editor may change, in vocabulary order: all of a
 * structure box's, and only the words of a leaf box (its label, its title),
 * never which data it shows. Empty for anything else.
 */
export function blockInputs(doc: StoryDoc, idx: number): BlockInput[] {
  const node = doc.nodes[idx]
  const spec = node ? STORY_VOCABULARY[node.tag] : undefined
  if (!spec) return []
  const out: BlockInput[] = []
  for (const [name, type] of Object.entries(spec.inputs)) {
    if (type === 'data' || type === 'flag') continue
    if (spec.kind === 'leaf' && type !== 'text') continue
    const input: BlockInput = Array.isArray(type)
      ? { name, type: 'choice', options: type, value: node.attrs[name] ?? '', required: spec.required.includes(name) }
      : { name, type, value: node.attrs[name] ?? '', required: spec.required.includes(name) }
    out.push(input)
  }
  return out
}

/**
 * Write the box settings: each input in `values` that differs from the source
 * is set, and one emptied is removed (a required one is kept, empty, for the
 * save check to name). Only the box's start tag changes.
 */
export function setInputs(source: string, idx: number, values: Record<string, string>, doc = parseStory(source)): string {
  let next = source
  let current = doc
  const required = STORY_VOCABULARY[doc.nodes[idx]?.tag ?? '']?.required ?? []
  for (const [name, raw] of Object.entries(values)) {
    const value = raw.trim()
    const had = current.nodes[idx].attrs[name]
    if (value === (had ?? '') && had !== undefined) continue
    if (value === '' && had === undefined) continue
    next = value === '' && !required.includes(name) ? removeAttr(next, idx, name, current) : setAttr(next, idx, name, value, current)
    current = parseStory(next)
  }
  return next
}

/**
 * The nearest box, `idx` itself or one around it, that has box settings (any
 * box with inputs the editor may change: a structure box, or a leaf with
 * words, like a chart's title), or -1.
 */
export function settingsHostOf(doc: StoryDoc, idx: number): number {
  for (let i = idx; i !== -1 && doc.nodes[i]; i = doc.nodes[i].parent) {
    if (blockInputs(doc, i).length > 0) return i
  }
  return -1
}

/** The attributes that say where a locked box's data comes from, for the lock badge. */
export function leafSourceAttrs(node: StoryNode): { name: string; value: string }[] {
  const names = ['from', 'row', 'col', 'rows', 'cols', 'type', 'x', 'highlight', 'change-from', 'change-row', 'change-col', 'compare-row', 'compare-col', 'metric']
  return names.filter((n) => node.attrs[n] !== undefined).map((n) => ({ name: n, value: node.attrs[n] }))
}

/** A leaf tag's name for a reader: pt-num and pt-kpi are both "Figure" (a single number), pt-chart is "Chart", pt-table is "Table". */
const LEAF_KIND: Record<string, string> = { 'pt-num': 'Figure', 'pt-kpi': 'Figure', 'pt-chart': 'Chart', 'pt-table': 'Table' }

/**
 * A leaf box's stable name for a reader with no title of its own: "Chart 2",
 * "Table 1", "Figure 3" — its 1-based position among every leaf box of the
 * same kind (`pt-num` and `pt-kpi` share "Figure"), in document order.
 * Undefined for a non-leaf element.
 */
export function leafOrdinalName(doc: StoryDoc, idx: number): string | undefined {
  const node = doc.nodes[idx]
  if (!node || !isLeafTag(node.tag)) return undefined
  const kind = LEAF_KIND[node.tag] ?? node.tag
  let n = 0
  for (const other of doc.nodes) {
    if (!isLeafTag(other.tag) || (LEAF_KIND[other.tag] ?? other.tag) !== kind) continue
    n++
    if (other.idx === idx) break
  }
  return `${kind} ${n}`
}
